import { NgOptimizedImage } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
  type ElementRef,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowLeft,
  heroArrowPath,
  heroArrowRightOnRectangle,
  heroAtSymbol,
  heroEye,
  heroEyeSlash,
  heroKey,
  heroLockClosed,
  heroShieldCheck,
  heroUserPlus,
} from '@ng-icons/heroicons/outline';
import { catchError, finalize, of, switchMap } from 'rxjs';
import type { AuthSessionResponse } from '../../core/api/generated/models/AuthSessionResponse';
import { resolveAppReturnPath } from '../../core/auth/auth.guard';
import { AuthStore } from '../../core/auth/auth.store';
import { CoreOnboardingApiService } from '../../core/api/core-onboarding-api.service';
import { PostHogService } from '../../core/services/posthog.service';
import {
  ToastrService,
  UiButton,
  UiIconButton,
  UiInput,
  UiOtpInput,
  UiOtpRoot,
  UiOtpSlot,
  UiOtpSlots,
} from '../../shared/ui';

type AuthMode =
  | 'login'
  | 'signup'
  | 'forgot-password'
  | 'reset-password'
  | 'email-code'
  | 'accept-invite';
/** Status of the /aceitar-convite preview lookup — drives the accept-invite mode's status text
 * (there is no form for this mode; it's a redirect-through screen). */
type AcceptInviteStatus = 'loading' | 'error';
/** Why the user landed on the email-code screen — purely cosmetic (tailors the heading), the
 * verification itself is identical either way. */
type EmailCodeReason = 'signup' | 'verify' | 'login';
type ExternalAuthMethod = 'email-link';

type ExternalAuthOption = {
  id: ExternalAuthMethod;
  label: string;
  ariaLabel: string;
  loadingLabel: string;
  mark: string;
};

type ConnectionPath = {
  id: string;
  d: string;
  pulseClass: string;
};

type ConnectionMap = {
  width: number;
  height: number;
  paths: readonly ConnectionPath[];
};

const EXTERNAL_AUTH_OPTIONS: readonly ExternalAuthOption[] = [
  {
    id: 'email-link',
    label: 'Código por e-mail',
    ariaLabel: 'Receber código por e-mail',
    loadingLabel: 'Enviando',
    mark: '@',
  },
];

const CONNECTION_PATH_VARIANTS = [
  { firstBend: 0.16, secondBend: 0.39, midWeight: 0.62 },
  { firstBend: 0.34, secondBend: 0.61, midWeight: 0.45 },
  { firstBend: 0.22, secondBend: 0.72, midWeight: 0.3 },
  { firstBend: 0, secondBend: 1, midWeight: 0 },
  { firstBend: 0.28, secondBend: 0.52, midWeight: 0.56 },
  { firstBend: 0.44, secondBend: 0.76, midWeight: 0.36 },
  { firstBend: 0.19, secondBend: 0.84, midWeight: 0.24 },
] as const;

@Component({
  selector: 'app-login-page',
  imports: [
    NgIcon,
    NgOptimizedImage,
    RouterLink,
    UiButton,
    UiIconButton,
    UiInput,
    UiOtpInput,
    UiOtpRoot,
    UiOtpSlot,
    UiOtpSlots,
  ],
  providers: [
    provideIcons({
      heroArrowLeft,
      heroArrowPath,
      heroArrowRightOnRectangle,
      heroAtSymbol,
      heroEye,
      heroEyeSlash,
      heroKey,
      heroLockClosed,
      heroShieldCheck,
      heroUserPlus,
    }),
  ],
  templateUrl: './login.page.html',
  styleUrl: './login.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage {
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly onboardingApi = inject(CoreOnboardingApiService);
  private readonly posthog = inject(PostHogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly authPageRef = viewChild.required<ElementRef<HTMLElement>>('authPage');
  private readonly formCardRef = viewChild.required<ElementRef<HTMLElement>>('formCard');
  private readonly connectionMarkerRef =
    viewChild.required<ElementRef<HTMLElement>>('connectionMarker');
  private connectionFrame = 0;

  readonly mode = signal<AuthMode>(this.resolveMode());
  readonly isSubmitting = signal(false);
  readonly isPasswordVisible = signal(false);
  readonly isConfirmPasswordVisible = signal(false);
  readonly externalAuthRedirecting = signal<ExternalAuthMethod | null>(null);
  readonly submitAttempted = signal(false);
  readonly isPointerActive = signal(false);
  readonly cursorX = signal('50%');
  readonly cursorY = signal('50%');
  readonly returnPath = signal(
    resolveAppReturnPath(this.route.snapshot.queryParamMap.get('returnTo')),
  );
  readonly email = signal(this.route.snapshot.queryParamMap.get('email') ?? '');
  readonly password = signal('');
  readonly confirmPassword = signal('');
  readonly firstName = signal('');
  readonly lastName = signal('');
  readonly token = signal(
    this.route.snapshot.queryParamMap.get('token') ??
      this.route.snapshot.queryParamMap.get('password_reset_token') ??
      '',
  );
  readonly code = signal('');
  readonly emailCodeReason = signal<EmailCodeReason>(this.resolveEmailCodeReason());
  /** Carried across signup/email-code/login as a query param so a pending invitation gets
   * accepted right after the session becomes ready — see handleAuthResponse(). */
  readonly inviteToken = signal(this.route.snapshot.queryParamMap.get('inviteToken') || null);
  readonly acceptInviteStatus = signal<AcceptInviteStatus>('loading');
  readonly acceptInviteError = signal('');
  readonly logoSrc = '/automato-logo.svg';
  readonly otpSlots = Array.from({ length: 6 }, (_, index) => index);
  readonly externalAuthOptions = EXTERNAL_AUTH_OPTIONS;
  readonly connectionMap = signal<ConnectionMap | null>(null);

  constructor() {
    afterNextRender(() => {
      if (this.mode() === 'accept-invite') {
        this.loadInvitePreview();
      }

      this.updateConnectionMap();

      const resizeObserver =
        typeof ResizeObserver === 'undefined'
          ? null
          : new ResizeObserver(() => this.scheduleConnectionMapUpdate());

      resizeObserver?.observe(this.authPageRef().nativeElement);
      resizeObserver?.observe(this.formCardRef().nativeElement);
      resizeObserver?.observe(this.connectionMarkerRef().nativeElement);

      window.addEventListener('resize', this.scheduleConnectionMapUpdate);

      this.destroyRef.onDestroy(() => {
        resizeObserver?.disconnect();
        window.removeEventListener('resize', this.scheduleConnectionMapUpdate);

        if (this.connectionFrame) {
          cancelAnimationFrame(this.connectionFrame);
        }
      });
    });
  }

  readonly title = computed(() => {
    switch (this.mode()) {
      case 'signup':
        return 'Criar conta';
      case 'forgot-password':
        return 'Redefinir senha';
      case 'reset-password':
        return 'Definir nova senha';
      case 'email-code':
        return 'Código de acesso';
      case 'accept-invite':
        return 'Convite';
      default:
        return 'Entrar';
    }
  });

  readonly description = computed(() => {
    switch (this.mode()) {
      case 'signup':
        return 'Crie sua identidade e avance para conectar ou criar o business.';
      case 'forgot-password':
        return 'Informe seu e-mail para receber o link de redefinição.';
      case 'reset-password':
        return 'Use o token recebido para proteger seu acesso com uma nova senha.';
      case 'email-code':
        switch (this.emailCodeReason()) {
          // Não afirma "criamos sua conta" — a API sempre responde igual aqui, mesmo quando o
          // e-mail já tinha conta (disfarce anti-enumeração, ver internal-auth.service.ts). A
          // frase evita a alegação falsa nesse caso E avisa quem já tem conta que o código entra
          // direto nela — sem revelar se o e-mail já existia ou não (D1/W1-8).
          case 'signup':
            return 'Enviamos um código de acesso para o seu e-mail. Se você já tem uma conta com este endereço, o código entra direto nela.';
          case 'verify':
            return 'Confirme seu e-mail com o código que enviamos.';
          default:
            return 'Digite o código enviado para seu e-mail.';
        }
      case 'accept-invite':
        return this.acceptInviteStatus() === 'error'
          ? 'Não foi possível validar este convite.'
          : 'Verificando seu convite…';
      default:
        return 'Use e-mail e senha para carregar seu contexto de trabalho.';
    }
  });

  readonly submitLabel = computed(() => {
    if (this.isSubmitting()) {
      return 'Processando';
    }

    switch (this.mode()) {
      case 'signup':
        return 'Criar conta';
      case 'forgot-password':
        return 'Enviar link';
      case 'reset-password':
        return 'Salvar senha';
      case 'email-code':
        return 'Confirmar acesso';
      default:
        return 'Entrar';
    }
  });

  readonly submitIcon = computed(() => {
    switch (this.mode()) {
      case 'signup':
        return 'heroUserPlus';
      case 'forgot-password':
      case 'reset-password':
        return 'heroArrowPath';
      case 'email-code':
        return 'heroShieldCheck';
      default:
        return 'heroArrowRightOnRectangle';
    }
  });

  readonly showsOAuth = computed(() => this.mode() === 'login');
  readonly isExternalAuthRedirecting = computed(() => this.externalAuthRedirecting() !== null);
  readonly showsBackButton = computed(() => this.mode() === 'email-code');

  readonly showsEmail = computed(
    () =>
      this.mode() === 'login' ||
      this.mode() === 'signup' ||
      this.mode() === 'forgot-password' ||
      this.mode() === 'email-code',
  );
  readonly showsPassword = computed(() => this.mode() === 'login' || this.mode() === 'signup');
  readonly showsOtp = computed(() => this.mode() === 'email-code');

  submit(event: SubmitEvent) {
    event.preventDefault();
    this.submitAttempted.set(true);
    this.toastr.clear();

    if (this.isEmailInvalid()) {
      this.showError('Informe um e-mail válido.');
      return;
    }

    if (this.isPasswordInvalid()) {
      this.showError(
        this.mode() === 'login'
          ? 'Digite sua senha para entrar.'
          : 'A senha precisa ter pelo menos 8 caracteres.',
      );
      return;
    }

    if (this.mode() === 'signup' && this.password() !== this.confirmPassword()) {
      this.showError('As senhas precisam ser iguais.');
      return;
    }

    if (this.mode() === 'reset-password' && !this.token()) {
      this.showError('Token de redefinição ausente.');
      return;
    }

    if (this.showsOtp() && this.code().length < 6) {
      this.showError('Informe o código completo.');
      return;
    }

    this.isSubmitting.set(true);

    switch (this.mode()) {
      case 'signup':
        this.signup();
        return;
      case 'forgot-password':
        this.requestPasswordReset();
        return;
      case 'reset-password':
        this.confirmPasswordReset();
        return;
      case 'email-code':
        this.verifyEmailCode();
        return;
      default:
        this.login();
    }
  }

  requestNewCode() {
    if (!isEmailAddress(this.email())) {
      this.showError('Informe um e-mail válido para reenviar o código.');
      return;
    }

    this.isSubmitting.set(true);
    this.toastr.clear();

    this.authStore
      .requestEmailCode({ email: this.email() })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.code.set('');
          this.submitAttempted.set(false);
          this.showSuccess('Enviamos um novo código para seu e-mail.');
        },
        error: () => this.showError('Não foi possível enviar o código agora.'),
      });
  }

  togglePasswordVisibility() {
    this.isPasswordVisible.update((visible) => !visible);
  }

  toggleConfirmPasswordVisibility() {
    this.isConfirmPasswordVisible.update((visible) => !visible);
  }

  startExternalLogin(_method: ExternalAuthMethod) {
    if (!isEmailAddress(this.email())) {
      this.submitAttempted.set(true);
      this.showError('Digite um e-mail válido para receber o código de acesso.');
      return;
    }

    this.externalAuthRedirecting.set('email-link');
    this.toastr.clear();
    const email = this.email();

    this.authStore
      .requestEmailCode({ email })
      .pipe(finalize(() => this.externalAuthRedirecting.set(null)))
      .subscribe({
        next: () => {
          this.posthog.posthog.capture('user_email_code_requested');
          void this.router.navigate(['/email-code'], {
            queryParams: { email, reason: 'login', inviteToken: this.inviteToken() },
          });
        },
        error: () => this.showError('Não foi possível enviar o código agora.'),
      });
  }

  goBackToLogin() {
    void this.router.navigate(['/login'], { queryParams: { inviteToken: this.inviteToken() } });
  }

  readInput(event: Event) {
    return event.target instanceof HTMLInputElement ? event.target.value : '';
  }

  trackPointer(event: PointerEvent) {
    if (!(event.currentTarget instanceof HTMLElement)) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    this.cursorX.set(`${event.clientX - rect.left}px`);
    this.cursorY.set(`${event.clientY - rect.top}px`);
    this.isPointerActive.set(true);
  }

  resetPointer() {
    this.isPointerActive.set(false);
  }

  isEmailInvalid() {
    return this.submitAttempted() && this.showsEmail() && !isEmailAddress(this.email());
  }

  isPasswordInvalid() {
    if (!this.submitAttempted() || (!this.showsPassword() && this.mode() !== 'reset-password')) {
      return false;
    }

    if (this.mode() === 'login') {
      return this.password().trim().length === 0;
    }

    return this.password().length < 8;
  }

  isConfirmPasswordInvalid() {
    return (
      this.submitAttempted() &&
      this.mode() === 'signup' &&
      (this.confirmPassword().length === 0 || this.password() !== this.confirmPassword())
    );
  }

  isOtpInvalid() {
    return this.submitAttempted() && this.showsOtp() && this.code().length < 6;
  }

  private readonly scheduleConnectionMapUpdate = () => {
    if (this.connectionFrame) {
      return;
    }

    this.connectionFrame = requestAnimationFrame(() => {
      this.connectionFrame = 0;
      this.updateConnectionMap();
    });
  };

  private updateConnectionMap() {
    const pageRect = this.authPageRef().nativeElement.getBoundingClientRect();
    const cardRect = this.formCardRef().nativeElement.getBoundingClientRect();
    const markerRect = this.connectionMarkerRef().nativeElement.getBoundingClientRect();
    const width = Math.max(1, Math.round(pageRect.width));
    const height = Math.max(1, Math.round(pageRect.height));
    const startX = clamp(cardRect.right - pageRect.left - 10, 0, width);
    const markerX = clamp(markerRect.left + markerRect.width / 2 - pageRect.left, startX + 24, width);
    const markerY = clamp(markerRect.top + markerRect.height / 2 - pageRect.top, 0, height);
    const pathEndX = width + 2;
    const travel = Math.max(24, markerX - startX);
    const spread = Math.min(320, Math.max(210, height * 0.36));
    const offsets = [
      -spread,
      -spread * 0.68,
      -spread * 0.34,
      0,
      spread * 0.34,
      spread * 0.68,
      spread,
    ];
    const pulseClasses = [
      'auth-connection-pulse--one',
      'auth-connection-pulse--two',
      'auth-connection-pulse--three',
      'auth-connection-pulse--four',
      'auth-connection-pulse--five',
      'auth-connection-pulse--six',
      'auth-connection-pulse--seven',
    ];
    const straightPathIndex = Math.floor(offsets.length / 2);

    const paths = offsets.map((offset, index) => {
      const startY = clamp(markerY + offset, 0, height);
      const variant = CONNECTION_PATH_VARIANTS[index] ?? CONNECTION_PATH_VARIANTS[0];
      const firstBendX = startX + travel * variant.firstBend;
      const secondBendX = startX + travel * variant.secondBend;
      const midY = markerY + offset * variant.midWeight;
      const d =
        index === straightPathIndex
          ? `M ${point(startX)} ${point(markerY)} H ${point(pathEndX)}`
          : [
              `M ${point(startX)} ${point(startY)}`,
              `H ${point(firstBendX)}`,
              `V ${point(midY)}`,
              `H ${point(secondBendX)}`,
              `V ${point(markerY)}`,
              `H ${point(pathEndX)}`,
            ].join(' ');

      return {
        id: `connection-${index}`,
        d,
        pulseClass: pulseClasses[index],
      };
    });

    this.connectionMap.set({ width, height, paths });
  }

  private login() {
    this.authStore
      .login({
        email: this.email(),
        password: this.password(),
      })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: (response) => {
          if (response.mode === 'ready' && response.session) {
            const user = response.session.user;
            this.posthog.posthog.identify(user.id, { email: user.email });
            this.posthog.posthog.capture('user_logged_in', { method: 'password' });
          }
          this.handleAuthResponse(response);
        },
        error: (error: unknown) => {
          if (readApiErrorCode(error) === 'auth_email_not_verified') {
            // The backend already issued+sent a fresh code as part of this login attempt.
            void this.router.navigate(['/email-code'], {
              queryParams: { email: this.email(), reason: 'verify', inviteToken: this.inviteToken() },
            });
            return;
          }

          this.showError('E-mail ou senha inválidos.');
        },
      });
  }

  private signup() {
    this.authStore
      .signup({
        email: this.email(),
        password: this.password(),
        firstName: this.firstName() || undefined,
        lastName: this.lastName() || undefined,
      })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          // pendingVerification is always true now — including when the email already had an
          // account (anti-enumeration: signup responds identically either way, and a login code
          // was sent instead of a distinct "already exists" error).
          this.posthog.posthog.capture('user_signed_up', { method: 'password' });
          void this.router.navigate(['/email-code'], {
            queryParams: { email: this.email(), reason: 'signup', inviteToken: this.inviteToken() },
          });
        },
        error: () => this.showError('Não foi possível criar a conta.'),
      });
  }

  private requestPasswordReset() {
    this.authStore
      .requestPasswordReset({ email: this.email() })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.posthog.posthog.capture('user_password_reset_requested');
          this.showSuccess('Se o e-mail existir, enviaremos as instruções de redefinição.');
        },
        error: () => this.showError('Não foi possível solicitar a redefinição agora.'),
      });
  }

  private confirmPasswordReset() {
    this.authStore
      .confirmPasswordReset({
        token: this.token(),
        password: this.password(),
      })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.showSuccess('Senha atualizada. Você será desconectado dos outros dispositivos.');
          void this.router.navigate(['/login']);
        },
        error: () => this.showError('Token inválido ou expirado.'),
      });
  }

  private verifyEmailCode() {
    this.authStore
      .verifyEmailCode({
        email: this.email(),
        code: this.code(),
      })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: (response) => {
          if (response.mode === 'ready' && response.session) {
            const user = response.session.user;
            this.posthog.posthog.identify(user.id, { email: user.email });
            this.posthog.posthog.capture('user_email_code_verified');
          }
          this.handleAuthResponse(response);
        },
        error: () => {
          this.code.set('');
          this.submitAttempted.set(false);
          this.showError('Código inválido ou expirado. Reenvie o código e tente novamente.');
        },
      });
  }

  private handleAuthResponse(response: AuthSessionResponse) {
    const inviteToken = this.inviteToken();
    if (inviteToken) {
      // A directed invite (this token) takes priority over generic domain-match discovery —
      // the visitor came here to join ONE specific business, not to pick from a list. Both
      // 'ready' and 'discovery' responses already carry an authenticated session, so accept
      // can run regardless of which one this was.
      this.acceptPendingInvite(inviteToken);
      return;
    }

    if (response.mode === 'ready' && response.session) {
      void this.router.navigateByUrl(
        this.authStore.isBusinessSetupRequired() ? '/onboarding' : this.returnPath(),
      );
      return;
    }

    void this.router.navigateByUrl('/onboarding');
  }

  private acceptPendingInvite(token: string) {
    this.onboardingApi
      .acceptInvitation({ token })
      .pipe(
        catchError(() => {
          this.showError('Não foi possível aceitar o convite agora. Tente novamente pelo link do e-mail.');
          return of(null);
        }),
        switchMap(() => this.authStore.loadSession()),
      )
      .subscribe(() => {
        void this.router.navigateByUrl(
          this.authStore.isBusinessSetupRequired() ? '/onboarding' : this.returnPath(),
        );
      });
  }

  private loadInvitePreview() {
    const token = this.token();
    if (!token) {
      this.acceptInviteStatus.set('error');
      this.acceptInviteError.set('Link de convite inválido.');
      return;
    }

    this.onboardingApi.previewInvitation(token).subscribe({
      next: (preview) => {
        void this.router.navigate([preview.hasAccount ? '/login' : '/signup'], {
          queryParams: { email: preview.email, inviteToken: token },
        });
      },
      error: () => {
        this.acceptInviteStatus.set('error');
        this.acceptInviteError.set(
          'Este convite não é mais válido. Peça um novo convite a quem te convidou.',
        );
      },
    });
  }

  private showError(message: string) {
    this.toastr.error(message);
  }

  private showSuccess(message: string) {
    this.toastr.success(message);
  }

  private resolveMode(): AuthMode {
    const mode = this.route.snapshot.data['mode'];

    if (
      mode === 'signup' ||
      mode === 'forgot-password' ||
      mode === 'reset-password' ||
      mode === 'email-code' ||
      mode === 'accept-invite'
    ) {
      return mode;
    }

    return 'login';
  }

  private resolveEmailCodeReason(): EmailCodeReason {
    const reason = this.route.snapshot.queryParamMap.get('reason');
    return reason === 'signup' || reason === 'verify' || reason === 'login' ? reason : 'login';
  }
}

function readApiErrorCode(error: unknown) {
  const responseBody = readErrorResponseBody(error);
  const code = isRecord(responseBody) ? responseBody['error'] : null;
  return typeof code === 'string' ? code : null;
}

function readErrorResponseBody(error: unknown) {
  return isRecord(error) && isRecord(error['error']) ? error['error'] : error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isEmailAddress(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function point(value: number) {
  return Number(value.toFixed(2));
}
